import type {
  AutoAssignmentApplyResult,
  AutoAssignmentPreview,
  AutoAssignmentPreviewFamily,
  AutoAssignmentPreviewProblem,
  AutoAssignmentPreviewSummary,
  CleaningAssignment,
  CleaningMoment,
  Exemption,
  ParentCleaningAssignment,
  SchoolTask,
  SchoolActivity,
  SchoolTaskSettings,
  SchoolTaskSignup,
  SchoolTaskStatus,
  SchoolTaskType,
  SchoolYear,
} from "./types";

type JsonObject = Record<string, unknown>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TASK_TYPES = new Set<SchoolTaskType>(["PARENT_TASK", "GENERAL_TASK", "HOLIDAY_CLEANING"]);

const object = (value: unknown): JsonObject | undefined =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as JsonObject
    : undefined;

const uuid = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  return UUID.test(normalized) ? normalized : undefined;
};

const text = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const optionalText = (value: unknown): string | undefined =>
  value == null ? undefined : text(value);

const boolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const number = (value: unknown): number | undefined => {
  const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : undefined;
};

const nonNegativeInteger = (value: unknown): number | undefined => {
  const parsed = number(value);
  return parsed !== undefined && Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined;
};

const positiveInteger = (value: unknown): number | undefined => {
  const parsed = nonNegativeInteger(value);
  return parsed !== undefined && parsed >= 1 ? parsed : undefined;
};

const dateTime = (value: unknown): string | undefined => {
  let parsed: Date;
  if (typeof value === "string" && value.trim()) {
    parsed = new Date(value);
  } else if (typeof value === "number" && Number.isFinite(value)) {
    // JavaTimeModule writes OffsetDateTime as epoch seconds while
    // WRITE_DATES_AS_TIMESTAMPS is enabled in the backend ObjectMapper.
    parsed = new Date(Math.abs(value) < 1e12 ? value * 1000 : value);
  } else if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = value;
    if (![year, month, day, hour, minute, second, nano].every((part) => typeof part === "number" && Number.isInteger(part))) return undefined;
    parsed = new Date(Date.UTC(year, month - 1, day, hour, minute, second, Math.floor(nano / 1_000_000)));
  } else {
    return undefined;
  }
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const localDate = (value: unknown): string | undefined => {
  let normalized: string;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    normalized = value;
  } else if (Array.isArray(value) && value.length === 3) {
    const [year, month, day] = value;
    if (![year, month, day].every((part) => typeof part === "number" && Number.isInteger(part))) return undefined;
    normalized = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } else {
    return undefined;
  }
  const parsed = new Date(`${normalized}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== normalized ? undefined : normalized;
};

const uuidArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const parsed = value.map(uuid);
  if (parsed.some((item) => !item)) return undefined;
  return Array.from(new Set(parsed as string[]));
};

export class SchoolTaskPayloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SchoolTaskPayloadError";
  }
}

export const parseRequired = <T>(value: unknown, parser: (item: unknown) => T | undefined): T => {
  const parsed = parser(value);
  if (parsed === undefined) throw new SchoolTaskPayloadError("Invalid school tasks response payload");
  return parsed;
};

export const parseList = <T>(value: unknown, parser: (item: unknown) => T | undefined): T[] => {
  if (!Array.isArray(value)) throw new SchoolTaskPayloadError("Expected a school tasks response array");
  return value.map((item) => parseRequired(item, parser));
};

// Collection endpoints can contain historical records that predate the
// current DTO contract. One malformed record must not hide every valid item
// from both portals. Keep the response shape strict, skip only invalid rows
// and emit a diagnostic without logging personal or task data.
export const parseReadableList = <T>(
  value: unknown,
  parser: (item: unknown) => T | undefined,
  collection: string,
): T[] => {
  if (!Array.isArray(value)) throw new SchoolTaskPayloadError("Expected a school tasks response array");
  const parsed = value.flatMap((item) => {
    const result = parser(item);
    return result === undefined ? [] : [result];
  });
  const skipped = value.length - parsed.length;
  if (value.length > 0 && parsed.length === 0) {
    throw new SchoolTaskPayloadError("Invalid school tasks response payload");
  }
  if (skipped > 0) {
    console.warn(`[schoolTasks] Skipped ${skipped} invalid ${collection} record(s).`);
  }
  return parsed;
};

// Supporting lookup endpoints can contain legacy records that are unrelated to
// the selected school-tasks year. Keep valid lookup values without weakening
// the strict parsing used for Schooltasks API contracts.
export const parseValidList = <T>(value: unknown, parser: (item: unknown) => T | undefined): T[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const parsed = parser(item);
    return parsed === undefined ? [] : [parsed];
  });
};

export const parseSchoolYear = (value: unknown): SchoolYear | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id);
  const startDate = localDate(item.startDate);
  const endDate = localDate(item.endDate);
  if (!id || !startDate || !endDate) return undefined;
  const fee = item.defaultMonthlyFee == null ? null : number(item.defaultMonthlyFee);
  if (item.defaultMonthlyFee != null && fee === undefined) return undefined;
  return { id, startDate, endDate, defaultMonthlyFee: fee };
};

export const parseSchoolActivity = (value: unknown): SchoolActivity | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id);
  const schoolYearId = uuid(item.schoolYearId);
  const name = text(item.name);
  const startsAt = item.startsAt == null ? null : dateTime(item.startsAt);
  const endsAt = item.endsAt == null ? null : dateTime(item.endsAt);
  if (!id || !schoolYearId || !name || (item.startsAt != null && !startsAt) || (item.endsAt != null && !endsAt)) return undefined;
  return { id, schoolYearId, name, description: optionalText(item.description), startsAt, endsAt };
};

export const parseSchoolTask = (value: unknown): SchoolTask | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id);
  const name = text(item.name);
  const schoolYearId = uuid(item.schoolYearId);
  const type = typeof item.type === "string" && TASK_TYPES.has(item.type as SchoolTaskType)
    ? item.type as SchoolTaskType
    : undefined;
  const capacity = positiveInteger(item.capacity);
  const signupCount = nonNegativeInteger(item.signupCount);
  const optional = boolean(item.optional);
  const startsAt = item.startsAt == null ? null : dateTime(item.startsAt);
  const classroomIds = uuidArray(item.classroomIds);
  const activity = item.activity == null ? null : parseSchoolActivity(item.activity);
  if (!id || !name || !schoolYearId || !type || !classroomIds || capacity === undefined || signupCount === undefined || optional === undefined || (item.startsAt != null && !startsAt) || (item.activity != null && !activity)) return undefined;
  return { id, name, description: optionalText(item.description), type, schoolYearId, classroomIds, startsAt, capacity, signupCount, optional, activity };
};

export const parseSchoolTaskSignup = (value: unknown): SchoolTaskSignup | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id); const taskId = uuid(item.taskId); const familyId = uuid(item.familyId);
  const childId = item.childId == null ? null : uuid(item.childId);
  return id && taskId && familyId && (item.childId == null || childId) ? { id, taskId, familyId, childId: childId || null } : undefined;
};

export const parseSchoolTaskStatus = (value: unknown): SchoolTaskStatus | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const familyId = uuid(item.familyId); const schoolYearId = uuid(item.schoolYearId);
  const exempt = boolean(item.exempt); const generalContributionComplete = boolean(item.generalContributionComplete);
  const generalContributionRequiredCount = nonNegativeInteger(item.generalContributionRequiredCount); const generalContributionCount = nonNegativeInteger(item.generalContributionCount);
  const cleaningAssignedCount = nonNegativeInteger(item.cleaningAssignedCount);
  // The children the family may be assigned for weekly cleaning, or why they cannot be determined.
  const cleaningChildIds = Array.isArray(item.cleaningChildIds) ? item.cleaningChildIds.map(uuid) : undefined;
  const cleaningChildrenUndetermined = item.cleaningChildrenUndetermined == null ? null : text(item.cleaningChildrenUndetermined);
  const overallComplete = boolean(item.overallComplete);
  if (!familyId || !schoolYearId || exempt === undefined || generalContributionComplete === undefined || generalContributionRequiredCount === undefined || generalContributionCount === undefined || cleaningAssignedCount === undefined || !cleaningChildIds || cleaningChildIds.some((id) => !id) || cleaningChildrenUndetermined === undefined || overallComplete === undefined) return undefined;
  const tasks = object(item.mandatoryParentTasks);
  if (!tasks) return undefined;
  const entries = Object.entries(tasks).map(([key, value]) => [uuid(key), boolean(value)] as const);
  if (entries.some(([childId, complete]) => !childId || complete === undefined)) return undefined;
  const mandatoryParentTasks = Object.fromEntries(entries as ReadonlyArray<readonly [string, boolean]>);
  return { familyId, schoolYearId, exempt, generalContributionComplete, generalContributionRequiredCount, generalContributionCount, mandatoryParentTasks, cleaningAssignedCount, cleaningChildIds: cleaningChildIds as string[], cleaningChildrenUndetermined, overallComplete };
};

export const parseCleaningMoment = (value: unknown): CleaningMoment | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id); const schoolYearId = uuid(item.schoolYearId); const startsAt = dateTime(item.startsAt); const capacity = positiveInteger(item.capacity);
  return id && schoolYearId && startsAt && capacity !== undefined
    ? { id, schoolYearId, startsAt, capacity, description: optionalText(item.description) }
    : undefined;
};

export const parseCleaningAssignment = (value: unknown): CleaningAssignment | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id); const momentId = uuid(item.momentId); const familyId = uuid(item.familyId); const childId = uuid(item.childId);
  return id && momentId && familyId && childId ? { id, momentId, familyId, childId } : undefined;
};

export const parseParentCleaningAssignment = (value: unknown): ParentCleaningAssignment | undefined => {
  const assignment = parseCleaningAssignment(value); const item = object(value);
  const startsAt = item && dateTime(item.startsAt);
  return assignment && startsAt ? { ...assignment, startsAt, description: optionalText(item?.description) } : undefined;
};

export const parseExemption = (value: unknown): Exemption | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const id = uuid(item.id); const familyId = uuid(item.familyId); const schoolYearId = uuid(item.schoolYearId);
  return id && familyId && schoolYearId ? { id, familyId, schoolYearId, reason: optionalText(item.reason) } : undefined;
};

const strictList = <T>(value: unknown, parser: (item: unknown) => T | undefined): T[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const parsed = value.map(parser);
  return parsed.some((item) => item === undefined) ? undefined : parsed as T[];
};

const parseAutoAssignmentSummary = (value: unknown): AutoAssignmentPreviewSummary | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const familiesConsidered = nonNegativeInteger(item.familiesConsidered);
  const proposedParentTasks = nonNegativeInteger(item.proposedParentTasks);
  const proposedGeneralContributions = nonNegativeInteger(item.proposedGeneralContributions);
  const unresolvedObligations = nonNegativeInteger(item.unresolvedObligations);
  if (familiesConsidered === undefined || proposedParentTasks === undefined || proposedGeneralContributions === undefined || unresolvedObligations === undefined) return undefined;
  return { familiesConsidered, proposedParentTasks, proposedGeneralContributions, unresolvedObligations };
};

const parseAutoAssignmentProblem = (value: unknown): AutoAssignmentPreviewProblem | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const code = text(item.code);
  const familyId = uuid(item.familyId);
  const childId = item.childId == null ? null : uuid(item.childId);
  const missingCount = nonNegativeInteger(item.missingCount);
  if (!code || !familyId || childId === undefined || missingCount === undefined) return undefined;
  return { code, familyId, childId, missingCount };
};

const parseAutoAssignmentFamily = (value: unknown): AutoAssignmentPreviewFamily | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const familyId = uuid(item.familyId);
  const parentTaskProposals = strictList(item.parentTaskProposals, (entry) => {
    const proposal = object(entry);
    const childId = uuid(proposal?.childId);
    const taskId = uuid(proposal?.taskId);
    return childId && taskId ? { childId, taskId } : undefined;
  });
  const generalContributionProposals = strictList(item.generalContributionProposals, (entry) => {
    const proposal = object(entry);
    const taskId = uuid(proposal?.taskId);
    const type = proposal?.type;
    return taskId && (type === "GENERAL_TASK" || type === "HOLIDAY_CLEANING") ? { taskId, type } as const : undefined;
  });
  const problems = strictList(item.problems, parseAutoAssignmentProblem);
  if (!familyId || !parentTaskProposals || !generalContributionProposals || !problems) return undefined;
  return { familyId, parentTaskProposals, generalContributionProposals, problems };
};

// The summary and the family list describe one calculation, so a single
// malformed family rejects the whole preview instead of being skipped.
export const parseAutoAssignmentPreview = (value: unknown): AutoAssignmentPreview | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const schoolYearId = uuid(item.schoolYearId);
  const generatedAt = dateTime(item.generatedAt);
  // Kept byte for byte: a later step sends it back to the backend.
  const inputFingerprint = typeof item.inputFingerprint === "string" && item.inputFingerprint.trim() ? item.inputFingerprint : undefined;
  const applyEnabled = boolean(item.applyEnabled);
  const selfSignupDeadline = localDate(item.selfSignupDeadline);
  const summary = parseAutoAssignmentSummary(item.summary);
  const families = strictList(item.families, parseAutoAssignmentFamily);
  if (!schoolYearId || !generatedAt || !inputFingerprint || applyEnabled === undefined || !selfSignupDeadline || !summary || !families) return undefined;
  return { schoolYearId, generatedAt, inputFingerprint, applyEnabled, selfSignupDeadline, summary, families };
};

export const parseSchoolTaskSettings = (value: unknown): SchoolTaskSettings | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const schoolYearId = uuid(item.schoolYearId);
  const selfSignupDeadline = item.selfSignupDeadline == null ? null : localDate(item.selfSignupDeadline);
  const selfSignupClosed = boolean(item.selfSignupClosed);
  const autoAssignmentApplyEnabled = boolean(item.autoAssignmentApplyEnabled);
  const parentAccessEnabled = boolean(item.parentAccessEnabled);
  if (!schoolYearId || selfSignupDeadline === undefined || selfSignupClosed === undefined || autoAssignmentApplyEnabled === undefined || parentAccessEnabled === undefined) return undefined;
  return { schoolYearId, selfSignupDeadline, selfSignupClosed, autoAssignmentApplyEnabled, parentAccessEnabled };
};

export const parseAutoAssignmentApplyResult = (value: unknown): AutoAssignmentApplyResult | undefined => {
  const item = object(value);
  if (!item) return undefined;
  const schoolYearId = uuid(item.schoolYearId);
  const assignedParentTasks = nonNegativeInteger(item.assignedParentTasks);
  const assignedGeneralContributions = nonNegativeInteger(item.assignedGeneralContributions);
  if (!schoolYearId || assignedParentTasks === undefined || assignedGeneralContributions === undefined) return undefined;
  return { schoolYearId, assignedParentTasks, assignedGeneralContributions };
};
